import { AbstractGameView } from "@lib/titan2-slingo/dist/game/view/abstract/display/AbstractGameView";
import { EventObject } from "@lib/titan2/dist/util/event/EventObject";
import { MouseUtil } from "@lib/titan2/dist/util/view/MouseUtil";
import { Point } from "pixi.js";
import { GreenFishPrizeType } from "../../../../../model/data/ChanceGamesData";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import gsap from "gsap";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import { addToTitanSpineSlot, delayFor, resetTitanSpine, SpineComplete, updateResponsiveSpineSlotsChildren } from "../../../../../utils/SpineUtils";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { Service } from "@lib/titan2-slingo/dist/api/Service";
import { OrientationType } from "@lib/titan2/dist/game/renderer/constant/RendererConstants";
import { ViewUtils } from "@lib/titan2/dist/util/view/ViewUtils";
import { GameJaspurrsCupsSoundEvents } from "../../../../../controller/event/GameSountEvent";
export var FeatureGreenFishState;
(function (FeatureGreenFishState) {
    FeatureGreenFishState[FeatureGreenFishState["IDLE"] = 0] = "IDLE";
    FeatureGreenFishState[FeatureGreenFishState["MOVING_TO_OFFSCREEN"] = 1] = "MOVING_TO_OFFSCREEN";
    FeatureGreenFishState[FeatureGreenFishState["MOVING_TO_WATCH_POSITION"] = 2] = "MOVING_TO_WATCH_POSITION";
})(FeatureGreenFishState || (FeatureGreenFishState = {}));
function resumeResponsiveTween(responsiveTween) {
    const tween = responsiveTween.tween;
    if (tween.targets()[0].destroyed) {
        return;
    }
    ;
    responsiveTween.assignVars(tween);
    const rawTime = tween.rawTime();
    tween.pause();
    tween.invalidate();
    tween.totalTime(rawTime);
    tween.resume();
}
export class FeatureGreenFish extends AbstractGameView {
    constructor(factory, landscapeProperties, portraitProperties) {
        super(factory, landscapeProperties, portraitProperties);
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(FeatureGreenFishEvents.FEATURE_PRESENT, this.featurePresent);
        this.registerGame(FeatureGreenFishEvents.FEATURE_START, this.featureStart);
        this.registerGame(FeatureGreenFishEvents.REVEAL_STANDARD_PRIZE, this.revealStandardPrize);
        this.registerGame(FeatureGreenFishEvents.REVEAL_COLLECT_ALL_PRIZE, this.revealCollectAllPrize);
        this.registerGame(FeatureGreenFishEvents.CLEAR, this.clear);
    }
    create() {
        super.create();
        this.visible = false;
        this.interactive = true;
        const numberOfPrizes = 9;
        this.pickedPrizeFields = [];
        for (let i = 0; i < numberOfPrizes; i++) {
            this.pickedPrizeFields.push(
            // use this as a parent so we can query the underlying pixiText object for it's resolution. remove from parent after
            TitanText.createField(this, this.landscapeProperties.pickedPrize, this.portraitProperties.pickedPrize));
            this.baseTextResolution = this.pickedPrizeFields[i].getPixiText().resolution;
            this.removeChild(this.pickedPrizeFields[i]);
        }
        this.missedPrizeFields = [];
        for (let i = 0; i < numberOfPrizes - 1; i++) {
            this.missedPrizeFields.push(TitanText.createField(null, this.landscapeProperties.missedPrize, this.portraitProperties.missedPrize));
        }
        this.collectAllCatnip = TitanSprite.createSprite(null, "collect-all-catnip", this.landscapeProperties.catnip, this.portraitProperties.catnip);
        this.collectAllCatnipRays = TitanSpine.createSpine(null, "collect-all-catnip-rays", this.landscapeProperties.catnipRays, this.portraitProperties.catnipRays);
        this.collectAllCatnip.addChildAt(this.collectAllCatnipRays, 0);
        TitanSprite.createSprite(this, "background", this.landscapeProperties.background, this.portraitProperties.background);
        this.cat = TitanSpine.createSpine(this, "cat", this.landscapeProperties.cat, this.portraitProperties.cat);
        const landscapeSpine = this.cat.getSpine(OrientationType.LANDSCAPE);
        const portraitSpine = this.cat.getSpine(OrientationType.PORTRAIT);
        landscapeSpine.skeleton.setSkinByName("landscape");
        portraitSpine.skeleton.setSkinByName("portrait");
        this.cups = this.landscapeProperties.cups.map((_, i) => {
            const cup = TitanSpine.createSpine(undefined, `cup-${i}`, this.landscapeProperties.cups[i], this.portraitProperties.cups[i]);
            addToTitanSpineSlot(this.cat, cup, `dynamic-cups`);
            return cup;
        });
        this.catSpaceContainer = ViewUtils.createContainer(this, "cat-space-container");
        this.titleText = TitanText.createField(this, "title-text", this.landscapeProperties.titleText, this.portraitProperties.titleText);
        this.helpText = TitanText.createField(this, "help-text", this.landscapeProperties.helpText, this.portraitProperties.helpText);
        this.logo = TitanSprite.createSprite(this, "logo", this.landscapeProperties.logoProperties, this.portraitProperties.logoProperties);
        this.helpButton = this.addChild(this.factory.createBonusHelp());
        this.handleResize();
    }
    async featurePresent() {
        this.cat.play("idle", true, false, 0);
        this.helpButton.setEnabled(!Service.isReplay());
        this.titleText.alpha = 0;
        this.helpText.alpha = 0;
        this.visible = true;
    }
    async featureStart() {
        await delayFor(1);
        await this.shuffleCups();
        gsap.to(this.helpText, {
            alpha: 1,
            duration: 0.5,
            delay: 5,
        });
        this.cups.forEach((cup, i) => {
            MouseUtil.addMouseDown(cup, () => this.dispatchGame(new EventObject(FeatureGreenFishEvents.PICK_SELECTED.getType(), { index: i })), null);
            MouseUtil.setInteractive(cup, !Service.isReplay());
        });
        gsap.delayedCall(0.1, () => this.dispatchGame(FeatureGreenFishEvents.PICKS_READY));
    }
    async shuffleCups() {
        this.dispatchGame(GameJaspurrsCupsSoundEvents.SHUFFLE);
        this.cat.play("mix up", false, false, 0);
        const center = this.cat.toGlobal(new Point(0, 70));
        const localCenter = this.cups[0].parent.toLocal(center);
        this.cups.forEach(cup => {
            gsap.to(cup, {
                pixi: {
                    x: localCenter.x,
                    y: localCenter.y
                },
                alpha: 0,
                duration: 0.25,
                delay: 0.2,
                ease: `expo.in`
            });
        });
        await delayFor(2.1);
        this.returnCupToPositionResponsiveTweens = this.cups.map(cup => {
            return {
                tween: gsap.to(cup, {
                    alpha: 1,
                    duration: 0.25,
                    ease: `expo.out`,
                    onComplete: () => this.returnCupToPositionResponsiveTweens = undefined
                }),
                assignVars: (tween) => {
                    tween.vars.pixi = {
                        x: cup.getProperties().position.x,
                        y: cup.getProperties().position.y
                    };
                }
            };
        });
        this.returnCupToPositionResponsiveTweens.forEach(tween => resumeResponsiveTween(tween));
        await SpineComplete(this.cat);
        this.cat.play("idle", true, false, 0);
    }
    clear() {
        this.visible = false;
        this.alpha = 1;
        this.missedPrizeCupReferences = [];
        this.returnCupToPositionResponsiveTweens = undefined;
        this.cupsRisingTweens = undefined;
        this.cupsFallingTweens = undefined;
        this.collectionTweens = undefined;
        this.collectAllScaleUpTweens = undefined;
        this.moveCatnipTween = undefined;
        this.movePickedPrizeTween = undefined;
        this.pickedPrizeFields.forEach(field => {
            field.parent?.removeChild(field);
            field.setText("");
            field.getPixiText().resolution = this.baseTextResolution;
            field.position.copyFrom(field.getProperties().position);
            field.scale.copyFrom(field.getProperties().scale);
        });
        this.missedPrizeFields.forEach(field => {
            field.parent?.removeChild(field);
            field.setText("");
            field.getPixiText().resolution = this.baseTextResolution;
            field.position.copyFrom(field.getProperties().position);
            field.scale.copyFrom(field.getProperties().scale);
        });
        this.cups.forEach(cup => {
            resetTitanSpine(cup);
            cup.position.copyFrom(cup.getProperties().position);
            cup.scale.copyFrom(cup.getProperties().scale);
        });
        resetTitanSpine(this.cat);
        resetTitanSpine(this.collectAllCatnipRays);
        this.catSpaceContainer.removeChildren();
        this.collectAllCatnip.parent?.removeChild(this.collectAllCatnip);
        this.collectAllCatnip.position.copyFrom(this.collectAllCatnip.getProperties().position);
        this.collectAllCatnip.scale.copyFrom(this.collectAllCatnip.getProperties().scale);
    }
    async revealStandardPrize(event) {
        gsap.killTweensOf(this.helpText);
        this.helpText.alpha = 0;
        this.helpButton.setEnabled(false);
        const payload = event.getPayload();
        const index = payload.index;
        const prize = payload.prize;
        const missedPrizes = payload.missedPrizes;
        this.cups.forEach(cup => {
            MouseUtil.setInteractive(cup, false);
        });
        await this.revealPickedPrize(index, prize);
        this.setMissedPrizes(index, missedPrizes);
        this.revealMissedPrizes(index, missedPrizes);
        await this.movePickedCashPrizeToDisplayLocation();
        await delayFor(1);
        this.cat.play("idle_to_excited_transition", false, false, 0);
        await SpineComplete(this.cat);
        this.cat.play("excited_idle", true, false, 0);
        await delayFor(3);
        this.dispatchGame(FeatureGreenFishEvents.REVEAL_PRIZE_COMPLETE);
    }
    async revealCollectAllPrize(event) {
        gsap.killTweensOf(this.helpText);
        this.helpText.alpha = 0;
        this.helpButton.setEnabled(false);
        const payload = event.getPayload();
        const index = payload.index;
        const prize = payload.prize;
        const missedPrizes = payload.missedPrizes;
        await this.revealPickedPrize(index, prize);
        this.cat.play("idle_to_excited_transition", false, false, 0);
        SpineComplete(this.cat, 0).then(() => this.cat.play("excited_idle", true, false, 0));
        await this.movePickedCollectAllCatnipTCentralLocation(index);
        SpineUtil.playLoop(this.collectAllCatnipRays, "loop", 0);
        SpineUtil.play(this.collectAllCatnipRays, "in", null, null, 1);
        this.dispatchGame(GameJaspurrsCupsSoundEvents.CATNIP_CELEBRATION);
        await delayFor(1);
        await this.movePickedCollectAllCatnipToDisplayLocation(index);
        gsap.delayedCall(1.75, () => this.dispatchGame(GameJaspurrsCupsSoundEvents.TABLE_FLIP));
        this.cat.play("table_flip", false, false, 0);
        SpineComplete(this.cat).then(() => this.cat.play("postflip_idle", true, false, 0));
        //todo play cat crazy idle after flip
        await delayFor(1.7);
        this.setMissedPrizes(index, missedPrizes);
        await this.cupsDropFromTableFlip(index);
        await this.accumulatePrizesFromFloor(index, missedPrizes);
        await delayFor(2);
        this.dispatchGame(FeatureGreenFishEvents.REVEAL_PRIZE_COMPLETE);
    }
    async cupsDropFromTableFlip(index) {
        const offsets = [
            0, 0, 0, 0,
            50, 50, 50,
            100, 100
        ];
        this.cupsRisingTweens = this.cups.map((cup, i) => {
            return {
                tween: gsap.to(cup, {
                    duration: 0.25,
                    ease: "sine.out"
                }),
                assignVars: (tween) => {
                    tween.vars.pixi = {
                        y: cup.y - 350
                    };
                }
            };
        });
        this.cupsRisingTweens.forEach(resumeResponsiveTween);
        await Promise.all(this.cupsRisingTweens.map(tween => tween.tween));
        this.cupsRisingTweens = undefined;
        this.cupsFallingTweens = this.cups.map((cup, i) => {
            return {
                tween: gsap.to(cup, {
                    duration: 0.4,
                    ease: "sine.in",
                }),
                assignVars: (tween) => {
                    tween.vars.pixi = {
                        y: this.uiProperties.cupFloorPosition.y + offsets[i]
                    };
                }
            };
        });
        this.cupsFallingTweens.forEach(({ tween }, i) => {
            tween.then(() => {
                if (i !== index) {
                    this.cups[i].play("reveal-all");
                }
            });
        });
        this.cupsFallingTweens.forEach(resumeResponsiveTween);
        await Promise.all(this.cupsFallingTweens.map(({ tween }) => tween));
        await delayFor(0.77);
    }
    async accumulatePrizesFromFloor(index, prizes) {
        const missedPrizeFields = prizes.map((_, i) => {
            return this.missedPrizeFields[i];
        });
        const cumulativePrizeValue = prizes.map((prize, i, allPrizes) => {
            return allPrizes.slice(0, i + 1).reduce((acc, curr) => acc + curr.value, 0);
        });
        const delayEase = gsap.parseEase("power1.out");
        this.collectionTweens = missedPrizeFields.flatMap((clone, i) => {
            return [
                {
                    tween: gsap.to(clone, {
                        duration: 1.4,
                        ease: "power4.in",
                        delay: delayEase(i / (missedPrizeFields.length - 1)) * 3,
                        onStart: () => gsap.delayedCall(1.4 - 0.279, () => this.dispatchGame(GameJaspurrsCupsSoundEvents.COLLECT_VALUE))
                    }),
                    assignVars: (tween) => {
                        this.catSpaceContainer.updateTransform();
                        const globalTargetPosition = this.catSpaceContainer.toGlobal(this.uiProperties.collectAllAcumulationCatSpacePosition);
                        clone.parent.updateTransform();
                        const localTargetPosition = clone.parent.toLocal(globalTargetPosition);
                        tween.vars.pixi = {
                            x: localTargetPosition.x,
                            y: localTargetPosition.y,
                            scale: 1
                        };
                    }
                }
            ];
        });
        this.collectAllScaleUpTweens = missedPrizeFields.map((clone, i) => {
            return {
                tween: gsap.to(missedPrizeFields[0], {
                    duration: 0.5,
                    ease: "elastic.out(1,0.5)",
                    delay: (delayEase(i / (missedPrizeFields.length - 1)) * 3) + 1.4,
                }),
                assignVars: (tween) => {
                    tween.vars.pixi = {
                        scale: 1 + (i * 0.2)
                    };
                }
            };
        });
        this.collectAllScaleUpTweens.forEach(resumeResponsiveTween);
        this.collectionTweens.forEach(resumeResponsiveTween);
        this.collectionTweens.forEach((collection, i) => {
            collection.tween.then(() => {
                if (i === 0) {
                    return;
                }
                else {
                    missedPrizeFields[i].setText("");
                    missedPrizeFields[0].setText(this.formatCurrency(cumulativePrizeValue[i]));
                }
            });
        });
        await Promise.all(this.collectionTweens.map(({ tween }) => tween));
    }
    setMissedPrizes(pickedIndex, missedPrizes) {
        this.missedPrizeCupReferences = [];
        missedPrizes.forEach((missedPrize, missIndex) => {
            const cup = this.cups[(missIndex >= pickedIndex) ? (missIndex + 1) : (missIndex)];
            const missedPrizeField = this.missedPrizeFields[missIndex];
            this.missedPrizeCupReferences[missIndex] = cup;
            if (missedPrize.type === GreenFishPrizeType.CASH) {
                missedPrizeField.setText(this.formatCurrency(missedPrize.value));
                missedPrizeField.pivot.set(0, missedPrizeField.getHeight() / 2);
                missedPrizeField.getPixiText().anchor.set(0.5);
                addToTitanSpineSlot(cup, missedPrizeField, `dynamic-value`);
            }
            else {
                addToTitanSpineSlot(cup, this.collectAllCatnip, `dynamic-value`);
            }
        });
    }
    async revealMissedPrizes(pickedIndex, missedPrizes) {
        const missedAnims = missedPrizes.map((missedPrize, missIndex) => {
            if (missIndex >= pickedIndex) {
                missIndex += 1;
            }
            const cup = this.cups[missIndex];
            cup.play(`reveal-miss`, false, false, 0);
            return SpineComplete(cup);
        });
        await Promise.all(missedAnims);
    }
    async revealPickedPrize(index, pickedPrize) {
        const cup = this.cups[index];
        const globalCupPosition = cup.parent.toGlobal(cup.position);
        const localToCatCupPosition = this.cat.toLocal(globalCupPosition);
        const cat = this.cat.getSpine();
        const bone = cat.skeleton.findBone("eye_controller");
        bone.x = bone.parent.worldToLocal(localToCatCupPosition.clone()).x;
        bone.y = bone.parent.worldToLocal(localToCatCupPosition.clone()).y;
        cat.update(0.01);
        if (pickedPrize.type === GreenFishPrizeType.CASH) {
            const prizeField = this.pickedPrizeFields[0];
            addToTitanSpineSlot(cup, prizeField, `dynamic-value`);
            prizeField.setText(pickedPrize.type === GreenFishPrizeType.CASH ? this.formatCurrency(pickedPrize.value) : "", true);
            prizeField.pivot.set(0, prizeField.getHeight() / 2);
            prizeField.getPixiText().anchor.set(0.5);
        }
        else {
            this.collectAllCatnip.scale.set(this.uiProperties.catnip.scale.x);
            addToTitanSpineSlot(cup, this.collectAllCatnip, `dynamic-value`);
        }
        gsap.killTweensOf([this.titleText, this.helpText]);
        gsap.to([this.titleText, this.helpText], {
            alpha: 0,
            duration: 0.5,
        });
        cup.play(`reveal-cash`, false, false, 0);
        this.dispatchGame(GameJaspurrsCupsSoundEvents.LASER);
        if (pickedPrize.type === GreenFishPrizeType.AWARD_ALL) {
            gsap.delayedCall(1, () => this.dispatchGame(GameJaspurrsCupsSoundEvents.CATNIP_UNCOVERED));
        }
        this.cat.play("laser_shoot", false, false, 0);
        await SpineComplete(this.cat);
        this.cat.play("idle", true, false, 0);
    }
    movePickedCashPrizeToDisplayLocation() {
        const prizeField = this.pickedPrizeFields[0];
        const clonedPrizeField = prizeField;
        this.dispatchGame(GameJaspurrsCupsSoundEvents.COLLECT_CASH_PRIZE);
        this.movePickedPrizeTween = {
            tween: gsap.to(clonedPrizeField, {
                ease: "power1.inOut",
                duration: 3
            }),
            assignVars: (tween) => {
                this.catSpaceContainer.updateTransform();
                clonedPrizeField.parent.updateTransform();
                const globalPosition = this.catSpaceContainer.toGlobal(this.uiProperties.pickedCashPrizeDisplayCatSpacePosition);
                const position = clonedPrizeField.parent.toLocal(globalPosition);
                tween.vars.motionPath = {
                    path: [
                        {
                            x: position.x,
                            y: position.y
                        }
                    ],
                    curviness: 2,
                    fromCurrent: true
                };
                tween.vars.pixi = { scale: 2.25 };
                if (clonedPrizeField.getPixiText() !== null) {
                    clonedPrizeField.getPixiText().resolution = this.baseTextResolution * 2.25;
                }
            },
        };
        resumeResponsiveTween(this.movePickedPrizeTween);
    }
    async movePickedCollectAllCatnipTCentralLocation(index) {
        this.moveSpriteToNewParent(this.collectAllCatnip, this.catSpaceContainer);
        this.moveCatnipTween = {
            tween: gsap.to(this.collectAllCatnip, {
                ease: "power1.inOut",
                duration: 1,
            }),
            assignVars: (tween) => {
                const landscape = this.renderer.getOrientation() === OrientationType.LANDSCAPE;
                tween.vars.motionPath = {
                    path: [
                        {
                            x: landscape ? 0 : 0,
                            y: landscape ? -320 : -320
                        }
                    ],
                    curviness: 2,
                    fromCurrent: true
                };
                tween.vars.pixi = { scale: this.portraitProperties.catnip.scale.x };
            },
        };
        resumeResponsiveTween(this.moveCatnipTween);
        await this.moveCatnipTween.tween;
    }
    async movePickedCollectAllCatnipToDisplayLocation(index) {
        this.moveCatnipTween = {
            tween: gsap.to(this.collectAllCatnip, {
                ease: "power1.inOut",
                duration: 1,
            }),
            assignVars: (tween) => {
                const landscape = this.renderer.getOrientation() === OrientationType.LANDSCAPE;
                tween.vars.motionPath = {
                    path: [
                        {
                            x: landscape ? 350 : 350,
                            y: landscape ? -500 : -500
                        }
                    ],
                    curviness: 2,
                    fromCurrent: true
                };
                tween.vars.pixi = { scale: this.portraitProperties.catnip.scale.x };
            },
        };
        resumeResponsiveTween(this.moveCatnipTween);
        await this.moveCatnipTween.tween;
    }
    handleResize(event) {
        super.handleResize(event);
        this.catSpaceContainer.position.set(this.cat.x, this.cat.y);
        this.catSpaceContainer.scale.set(this.cat.scale.x, this.cat.scale.y);
    }
    handleRotate(event) {
        super.handleRotate(event);
        this.catSpaceContainer.position.set(this.cat.x, this.cat.y);
        this.catSpaceContainer.scale.set(this.cat.scale.x, this.cat.scale.y);
        updateResponsiveSpineSlotsChildren(this.cat);
        this.cups.forEach(cup => updateResponsiveSpineSlotsChildren(cup));
        if (this.returnCupToPositionResponsiveTweens) {
            this.returnCupToPositionResponsiveTweens.forEach(resumeResponsiveTween);
        }
        if (this.movePickedPrizeTween) {
            resumeResponsiveTween(this.movePickedPrizeTween);
        }
        if (this.moveCatnipTween) {
            resumeResponsiveTween(this.moveCatnipTween);
        }
        if (this.cupsRisingTweens) {
            this.cupsRisingTweens.forEach(resumeResponsiveTween);
        }
        if (this.cupsFallingTweens) {
            this.cupsFallingTweens.forEach(resumeResponsiveTween);
        }
        if (this.collectionTweens) {
            this.collectionTweens.forEach(resumeResponsiveTween);
        }
        if (this.collectAllScaleUpTweens) {
            this.collectAllScaleUpTweens.forEach(resumeResponsiveTween);
        }
    }
    moveSpriteToNewParent(sprite, parent) {
        sprite.updateTransform();
        const originalBaseScale = sprite.scale.clone();
        const worldPosition = sprite.parent.toGlobal(sprite.position);
        const originalWorldScale = sprite.worldTransform.a;
        parent.addChild(sprite);
        sprite.updateTransform();
        const newWorldScale = sprite.worldTransform.a;
        sprite.position.copyFrom(parent.toLocal(worldPosition));
        sprite.scale.set((originalWorldScale / newWorldScale) * originalBaseScale.x, (originalWorldScale / newWorldScale) * originalBaseScale.y);
    }
}
FeatureGreenFish.REPLAY_PICK_INDEX = 6;
export class FeatureGreenFishEvents {
}
FeatureGreenFishEvents.FEATURE_PRESENT = new EventObject("FeatureGreenFishEvents.FEATURE_PRESENT");
FeatureGreenFishEvents.FEATURE_START = new EventObject("FeatureGreenFishEvents.FEATURE_START");
FeatureGreenFishEvents.PICKS_READY = new EventObject("FeatureGreenFishEvents.PICKS_READY");
FeatureGreenFishEvents.PICK_SELECTED = new EventObject("FeatureGreenFishEvents.PICK_SELECTED");
FeatureGreenFishEvents.REVEAL_STANDARD_PRIZE = new EventObject("FeatureGreenFishEvents.REVEAL_STANDARD_PRIZE");
FeatureGreenFishEvents.REVEAL_COLLECT_ALL_PRIZE = new EventObject("FeatureGreenFishEvents.REVEAL_COLLECT_ALL_PRIZE");
FeatureGreenFishEvents.REVEAL_PRIZE_COMPLETE = new EventObject("FeatureGreenFishEvents.REVEAL_PRIZE_COMPLETE");
FeatureGreenFishEvents.CLEAR = new EventObject("FeatureGreenFishEvents.CLEAR");
//# sourceMappingURL=FeatureGreenFish.js.map